import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import * as THREE from "three";

/* ═══════════════════════════════════════════════════════════════
   Pure Three.js scene — no react-three-fiber required.
   Renders a cinematic backdrop: floating torus knot, orbiting
   cubes, and 3200 depth-cued particles. Reactive to pointer.
   ═══════════════════════════════════════════════════════════════ */
function useThreeScene(containerRef) {
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color("#05070c");
    scene.fog = new THREE.Fog("#05070c", 8, 32);

    const camera = new THREE.PerspectiveCamera(55, el.clientWidth / el.clientHeight, 0.1, 100);
    camera.position.set(0, 0, 7.5);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false, powerPreference: "high-performance" });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.6));
    renderer.setSize(el.clientWidth, el.clientHeight);
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.1;
    el.appendChild(renderer.domElement);

    // Lights
    scene.add(new THREE.AmbientLight(0xffffff, 0.28));
    const l1 = new THREE.PointLight(0x00e5ff, 40, 20); l1.position.set(6, 6, 6); scene.add(l1);
    const l2 = new THREE.PointLight(0x2b6cff, 30, 20); l2.position.set(-6, -3, -4); scene.add(l2);
    const l3 = new THREE.PointLight(0xff6a2c, 12, 15); l3.position.set(0, -6, 6); scene.add(l3);

    // ── Torus Knot (hero object)
    const tkGeo = new THREE.TorusKnotGeometry(1.6, 0.28, 220, 32, 2, 3);
    const tkMat = new THREE.MeshStandardMaterial({
      color: 0x0a1226, emissive: 0x2b6cff, emissiveIntensity: 0.5,
      metalness: 0.95, roughness: 0.15,
    });
    const tk = new THREE.Mesh(tkGeo, tkMat);
    scene.add(tk);

    const tkWireMat = new THREE.MeshBasicMaterial({ color: 0x00e5ff, wireframe: true, transparent: true, opacity: 0.16 });
    const tkWire = new THREE.Mesh(tkGeo, tkWireMat);
    scene.add(tkWire);

    // ── Orbiting cubes
    const cubeGroup = new THREE.Group();
    const cubeGeo = new THREE.BoxGeometry(0.35, 0.35, 0.35);
    const cubes = [];
    for (let i = 0; i < 6; i++) {
      const angle = (i / 6) * Math.PI * 2;
      const r = 4.2 + (i % 2) * 0.8;
      const mat = new THREE.MeshStandardMaterial({
        color: 0x08111f,
        emissive: i % 2 === 0 ? 0x00e5ff : 0xff6a2c,
        emissiveIntensity: 0.6,
        metalness: 0.9, roughness: 0.2,
      });
      const cube = new THREE.Mesh(cubeGeo, mat);
      cube.position.set(Math.cos(angle) * r, Math.sin(angle * 2) * 0.9, Math.sin(angle) * r);
      cube.userData = { angle, r, phase: i };
      cubeGroup.add(cube);
      cubes.push(cube);
    }
    scene.add(cubeGroup);

    // ── Particle field (spherical shell)
    const particleCount = 3200;
    const positions = new Float32Array(particleCount * 3);
    for (let i = 0; i < particleCount; i++) {
      const r = 8 + Math.random() * 22;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      positions[i * 3]     = r * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta) * 0.6;
      positions[i * 3 + 2] = r * Math.cos(phi);
    }
    const pGeo = new THREE.BufferGeometry();
    pGeo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    const pMat = new THREE.PointsMaterial({
      color: 0x00e5ff, size: 0.035, sizeAttenuation: true,
      transparent: true, opacity: 0.9, depthWrite: false,
    });
    const particles = new THREE.Points(pGeo, pMat);
    scene.add(particles);

    // ── Pointer parallax
    const pointer = { x: 0, y: 0, tx: 0, ty: 0 };
    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      pointer.tx = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
      pointer.ty = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
    };
    window.addEventListener("pointermove", onMove);

    // ── Resize
    const onResize = () => {
      if (!el) return;
      const w = el.clientWidth;
      const h = el.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener("resize", onResize);

    // ── Animation loop
    const clock = new THREE.Clock();
    let raf = 0;
    const tick = () => {
      const t = clock.getElapsedTime();
      const dt = Math.min(clock.getDelta() || 0.016, 0.04);

      // Pointer easing
      pointer.x += (pointer.tx - pointer.x) * 0.06;
      pointer.y += (pointer.ty - pointer.y) * 0.06;

      camera.position.x = pointer.x * 0.6;
      camera.position.y = -pointer.y * 0.4;
      camera.lookAt(0, 0, 0);

      // Torus knot
      tk.rotation.y += dt * 0.4;
      tk.rotation.x = Math.sin(t * 0.2) * 0.18;
      tk.position.y = Math.sin(t * 0.7) * 0.15;
      tkWire.rotation.copy(tk.rotation);
      tkWire.position.copy(tk.position);

      // Cubes
      cubeGroup.rotation.y -= dt * 0.18;
      cubes.forEach((c, i) => {
        c.rotation.x += dt * (0.6 + i * 0.05);
        c.rotation.y += dt * (0.4 + i * 0.03);
        c.position.y = Math.sin(t * 1.2 + i) * 0.9;
      });

      // Particles
      particles.rotation.y += dt * 0.03;
      particles.rotation.x += dt * 0.006;

      renderer.render(scene, camera);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("resize", onResize);
      renderer.dispose();
      tkGeo.dispose();
      tkMat.dispose();
      tkWireMat.dispose();
      cubeGeo.dispose();
      pGeo.dispose();
      pMat.dispose();
      cubes.forEach((c) => c.material.dispose());
      if (el && renderer.domElement.parentNode === el) el.removeChild(renderer.domElement);
    };
  }, [containerRef]);
}

/* ---------- Hero ---------- */
export default function Hero() {
  const canvasRef = useRef(null);
  useThreeScene(canvasRef);

  return (
    <section
      id="top"
      className="relative min-h-screen w-full overflow-hidden vw-noise"
      data-testid="vw-hero"
    >
      {/* 3D Canvas backdrop */}
      <div ref={canvasRef} className="absolute inset-0" />

      {/* Radial vignette */}
      <div className="absolute inset-0 pointer-events-none" style={{
        background: "radial-gradient(ellipse 90% 65% at 50% 55%, transparent 0%, rgba(5,7,12,0.55) 55%, #05070c 92%)"
      }} />

      {/* Grid overlay */}
      <div className="absolute inset-0 vw-grid opacity-40 pointer-events-none" />

      {/* Scanline top bar */}
      <div className="absolute top-[52px] left-0 right-0 h-8 flex items-center justify-between px-6 md:px-14 font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)] pointer-events-none z-20">
        <span className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 bg-[var(--vw-cyan)] vw-blink" />
          STUDIOS · MUMBAI · BHOPAL · LOS ANGELES REP · WORLDWIDE DELIVERY
        </span>
        <span className="hidden md:inline">2.39 : 1  ·  24 FPS  ·  DCP-READY</span>
      </div>

      {/* Hero content */}
      <div className="relative z-10 min-h-screen flex flex-col justify-center pt-[52px] pb-24">
        <div className="vw-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="vw-eyebrow mb-8"
            data-testid="vw-hero-eyebrow"
          >
            EST. 2013 &nbsp;·&nbsp; 150+ ARTISTS &nbsp;·&nbsp; 4 CONTINENTS DELIVERED
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="font-display text-[13vw] sm:text-[10vw] lg:text-[8.2vw] leading-[0.86] tracking-[-0.045em] text-[var(--vw-text)] max-w-[16ch]"
            data-testid="vw-hero-title"
          >
            90-minute
            <br />
            CGI features.
            <br />
            <span className="vw-text-gradient">Under $2M</span>
            <span className="text-[var(--vw-cyan)]">.</span>
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.55, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 max-w-[1100px]"
          >
            <p className="font-serif-italic text-2xl md:text-3xl lg:text-[2rem] leading-[1.25] text-[var(--vw-text-dim)] max-w-[32ch]">
              A 150-artist studio delivering theatrical animation at 40–60% below US and European budgets — screenplay to DCP, under one roof.
            </p>
            <div className="hidden lg:flex flex-col gap-1 font-mono text-[10px] uppercase tracking-[0.24em] text-[var(--vw-text-mute)] min-w-[240px]">
              <div className="flex justify-between border-b border-[var(--vw-border)] pb-2">
                <span>90-MIN FEATURE</span><span className="text-[var(--vw-cyan)]">UNDER $2M</span>
              </div>
              <div className="flex justify-between border-b border-[var(--vw-border)] py-2">
                <span>ARTISTS ON FLOOR</span><span className="text-[var(--vw-text)]">150+</span>
              </div>
              <div className="flex justify-between border-b border-[var(--vw-border)] py-2">
                <span>MASTERS</span><span className="text-[var(--vw-text)]">4K · DOLBY ATMOS</span>
              </div>
              <div className="flex justify-between py-2">
                <span>DELIVERY</span><span className="text-[var(--vw-text)]">DCP · IMF · PRORES</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.8 }}
            className="mt-12 flex flex-wrap items-center gap-4"
          >
            <a href="/estimator" className="vw-btn-primary vw-focus" data-testid="vw-hero-cta-primary">
              Get a Budget Estimate
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 12h14M13 6l6 6-6 6"/>
              </svg>
            </a>
            <a href="#showreel" className="vw-btn-ghost vw-focus" data-testid="vw-hero-cta-secondary">
              <span className="w-6 h-6 grid place-items-center border border-current rounded-full">
                <svg width="8" height="8" viewBox="0 0 10 10" fill="currentColor"><path d="M2 1v8l7-4z"/></svg>
              </span>
              Watch the Reel
              <span className="opacity-50">02:25</span>
            </a>
          </motion.div>
        </div>
      </div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 z-20"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[var(--vw-text-mute)]">
          Scroll · Explore
        </span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="w-px h-10 bg-gradient-to-b from-[var(--vw-cyan)] to-transparent"
        />
      </motion.div>
    </section>
  );
}
